import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ChevronRight, Upload, ShieldAlert, ScrollText, ArrowRight, MapPin } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { stateLeaseLaws, PSEO_YEAR, type StateLeaseLaw } from "@/data/stateLeaseLaws";

export const Route = createFileRoute("/lease-red-flags/$state")({
  loader: ({ params }: { params: { state: string } }) => {
    const data = stateLeaseLaws[params.state];
    if (!data) throw notFound();
    return { data };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Lease Red Flags" }] };
    const { data } = loaderData;
    const title = `${data.state} Lease Red Flags — ${data.redFlagCount} Clauses Tenants Should Refuse in ${PSEO_YEAR}`;
    const desc = `${data.state} tenant law guide for ${PSEO_YEAR}. Spot illegal lease clauses, deposit caps, notice rules, and what to negotiate before signing.`;
    const url = `https://legaldecoder.io/lease-red-flags/${data.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: data.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://legaldecoder.io/" },
              {
                "@type": "ListItem",
                position: 2,
                name: "Lease Red Flags",
                item: "https://legaldecoder.io/lease-red-flags",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: data.state,
                item: `https://legaldecoder.io/lease-red-flags/${data.slug}`,
              },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-3xl font-bold">State guide not available yet</h1>
        <p className="mt-3 text-muted-foreground">
          We're rolling these out state by state. Try the general lease decoder in the meantime.
        </p>
        <Link
          to="/tools/$slug"
          params={{ slug: "lease-decoder" }}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
        >
          Try Lease Decoder <ArrowRight className="h-4 w-4" />
        </Link>
      </main>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-2xl font-bold">Something went wrong</h1>
        <p className="mt-3 text-muted-foreground">{error.message}</p>
      </main>
    </div>
  ),
  component: StateLeasePage,
});

function StateLeasePage() {
  const { data } = Route.useLoaderData() as { data: StateLeaseLaw };
  const title = `${data.state} Lease Red Flags — ${data.redFlagCount} Clauses Tenants Should Refuse in ${PSEO_YEAR}`;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Breadcrumb */}
        <div className="mx-auto max-w-4xl px-4 pt-8 sm:px-6 lg:px-8">
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-1 text-xs text-muted-foreground"
          >
            <Link to="/" className="hover:text-foreground">
              Home
            </Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/blog" className="hover:text-foreground">
              Lease Red Flags
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span className="font-medium text-foreground">{data.state}</span>
          </nav>
        </div>

        {/* Header */}
        <header className="mx-auto max-w-4xl px-4 pb-8 pt-6 sm:px-6 lg:px-8">
          <div className="flex items-start gap-5">
            <div
              className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-border bg-gradient-to-br from-primary/10 to-primary-glow/10 text-primary-glow"
              aria-hidden
            >
              <MapPin className="h-7 w-7" strokeWidth={2.25} />
              <span className="sr-only">{data.state} outline</span>
            </div>
            <div className="min-w-0">
              <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <ShieldAlert className="h-3.5 w-3.5 text-primary-glow" />
                {data.state} · {PSEO_YEAR} tenant law
              </div>
              <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{title}</h1>
              <p className="mt-3 text-muted-foreground">
                A plain-English guide to {data.state}'s most-violated lease rules — and the clauses
                smart tenants refuse to sign.
              </p>
            </div>
          </div>
        </header>

        {/* Upload CTA */}
        <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-card to-primary-glow/10 p-6 shadow-card sm:p-7">
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow shadow-elegant">
                  <Upload className="h-5 w-5 text-primary-foreground" strokeWidth={2.25} />
                </div>
                <div>
                  <h2 className="text-lg font-bold sm:text-xl">Have a {data.state} lease?</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Upload it now — our AI knows {data.state} tenant law and flags violations in
                    seconds.
                  </p>
                </div>
              </div>
              <Link
                to="/"
                hash="analyzer"
                className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition-all hover:scale-105 sm:w-auto"
              >
                Analyze My {data.abbr} Lease
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Laws */}
        <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            {data.laws.length} {data.state} laws every tenant should know
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {data.laws.map((law, i) => (
              <article
                key={law.title}
                className="rounded-2xl border border-border bg-card p-5 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
              >
                <div className="mb-2 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary-glow">
                  <ScrollText className="h-3.5 w-3.5" />
                  Law {i + 1}
                </div>
                <h3 className="text-base font-semibold text-foreground">{law.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{law.summary}</p>
                {law.statute && (
                  <p className="mt-3 text-xs font-medium text-muted-foreground/80">{law.statute}</p>
                )}
              </article>
            ))}
          </div>
        </section>

        {/* Comparison Table */}
        <section className="bg-surface/40 py-14">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              {data.state} law vs. the clause your landlord may try
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              If your lease looks like the right column, push back — or have us scan it for free.
            </p>
            <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card shadow-card">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface/60 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 sm:px-5">Topic</th>
                    <th className="px-4 py-3 sm:px-5">{data.state} law</th>
                    <th className="px-4 py-3 sm:px-5">Common lease clause</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.comparison.map((row) => (
                    <tr key={row.topic}>
                      <td className="px-4 py-3 font-medium text-foreground sm:px-5">{row.topic}</td>
                      <td className="px-4 py-3 text-emerald-600 dark:text-emerald-400 sm:px-5">
                        {row.law}
                      </td>
                      <td className="px-4 py-3 text-rose-600 dark:text-rose-400 sm:px-5">
                        {row.commonClause}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">{data.state} lease FAQ</h2>
          <Accordion
            type="single"
            collapsible
            className="mt-6 rounded-2xl border border-border bg-card px-6 shadow-card"
          >
            {data.faqs.map((f) => (
              <AccordionItem key={f.q} value={f.q} className="last:border-b-0">
                <AccordionTrigger className="py-5 text-left text-base font-semibold">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-muted-foreground">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Related */}
        <section className="bg-surface/40 py-14">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl font-bold tracking-tight sm:text-2xl">Keep reading</h2>
            <ul className="mt-5 grid gap-3 sm:grid-cols-3">
              {data.relatedArticles.map((a) => (
                <li key={a.href}>
                  <a
                    href={a.href}
                    className="group flex h-full flex-col rounded-2xl border border-border bg-card p-5 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
                  >
                    <span className="text-sm font-semibold text-foreground">{a.title}</span>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary-glow">
                      Read article{" "}
                      <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-center text-[11px] uppercase tracking-wider text-muted-foreground">
              Not legal advice · For informational purposes only
            </p>
          </div>
        </section>
        <Footer />
      </main>
    </div>
  );
}
